import Image from "next/image";
import Link from "next/link";
import { Baby, CalendarDays, Clock, MapPin, Palmtree, Sun } from "lucide-react";
import SectionTitle from "../shared/SectionTitle";
import PageSection from "@/components/shared/page-section";
import { schoolContact } from "@/lib/school-contact";
import sticker5 from "../../../public/sticker5.png";
import sticker6 from "../../../public/sticker6.png";

const DATES = [
	{
		weekday: "Sabato",
		day: "14",
		month: "Novembre",
		year: "2026",
		time: "10:00 – 12:30",
		accent: "#22c55e",
	},
	{
		weekday: "Sabato",
		day: "5",
		month: "Dicembre",
		year: "2026",
		time: "10:00 – 12:30",
		accent: "#10b0ff",
	},
	{
		weekday: "Sabato",
		day: "16",
		month: "Gennaio",
		year: "2027",
		time: "10:00 – 12:30",
		accent: "#e11d48",
	},
] as const;

const HIGHLIGHTS = [
	"Ricevere tutte le informazioni sull'offerta didattica",
	"Visitare gli ambienti educativi e gli spazi all'aperto",
	"Conoscere le insegnanti e il progetto della scuola",
];

const SERVICES = [
	{
		label: "Sezione Primavera",
		detail: "24–36 mesi",
		icon: Baby,
	},
	{
		label: "Scuola dell'infanzia",
		detail: "3–6 anni",
		icon: CalendarDays,
	},
	{
		label: "Doposcuola",
		detail: "Pomeriggio in continuità",
		icon: Sun,
	},
	{
		label: "Centro estivo",
		detail: "Estate a Santa Marta",
		icon: Palmtree,
	},
];

const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(schoolContact.addressLine)}`;

export default function OpenDay() {
	return (
		<div className="bg-background">
			<section className="bg-[#d0122d]" aria-label="Locandina Open Day">
				<div className="relative mx-auto w-full max-w-[1400px] md:hidden aspect-square">
					<Image
						src="/openday/openday-locandina.jpg"
						alt="Open Day Scuola Santa Marta: 14 novembre 2026, 5 dicembre 2026 e 16 gennaio 2027, dalle 10:00 alle 12:30"
						fill
						priority
						sizes="100vw"
						className="object-contain"
					/>
				</div>
				<div className="relative mx-auto hidden w-full max-w-[1400px] md:block aspect-[1024/574]">
					<Image
						src="/openday/openday-banner.jpg"
						alt="Open Day Scuola Santa Marta: 14 novembre 2026, 5 dicembre 2026 e 16 gennaio 2027, dalle 10:00 alle 12:30"
						fill
						priority
						sizes="100vw"
						className="object-contain"
					/>
				</div>
			</section>

			<PageSection>
				<SectionTitle
					title1="Vieni a "
					title2="trovarci"
					leftIcon={sticker5}
					rightIcon={sticker6}
					iconSizeLeft={130}
					iconSizeRight={130}
					hideIconOnMobile="left"
				/>

				<p className="mt-4 mx-auto max-w-3xl text-center text-lg text-foreground leading-relaxed">
					Tre sabati per entrare nella scuola, respirare il clima di Casa
					Betania e capire se Santa Marta è il posto giusto per il tuo
					bambino. Accogliamo le famiglie della Sezione Primavera e della
					Scuola dell&apos;infanzia.
				</p>

				<div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
					{DATES.map((date) => (
						<article
							key={`${date.day}-${date.month}`}
							className="overflow-hidden rounded-2xl bg-white text-center shadow-md"
							style={{ borderTop: `8px solid ${date.accent}` }}
						>
							<div className="px-5 py-7">
								<p
									className="text-sm font-bold uppercase tracking-[0.2em]"
									style={{ color: date.accent }}
								>
									{date.weekday}
								</p>
								<p
									className="mt-1 text-6xl font-extrabold leading-none"
									style={{ color: date.accent }}
								>
									{date.day}
								</p>
								<p className="mt-2 text-xl font-semibold text-foreground">
									{date.month} {date.year}
								</p>
								<p className="mt-3 inline-flex items-center gap-2 text-muted-foreground">
									<Clock className="size-4" aria-hidden />
									{date.time}
								</p>
							</div>
						</article>
					))}
				</div>
			</PageSection>

			<PageSection className="pt-0">
				<div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
					<div>
						<h2 className="text-3xl font-bold text-center lg:text-left">
							<span className="text-foreground">Cosa faremo </span>
							<span className="text-(--card-foreground)">insieme</span>
						</h2>
						<ul className="mt-6 space-y-4 text-lg text-foreground">
							{HIGHLIGHTS.map((item) => (
								<li key={item} className="flex gap-3">
									<span
										className="mt-2 size-2.5 shrink-0 rounded-full bg-(--card-foreground)"
										aria-hidden
									/>
									{item}
								</li>
							))}
						</ul>

						<div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
							{SERVICES.map(({ label, detail, icon: Icon }) => (
								<div
									key={label}
									className="flex items-center gap-3 rounded-xl border border-border bg-white px-4 py-3"
								>
									<Icon
										className="size-6 shrink-0 text-(--card-foreground)"
										aria-hidden
									/>
									<div>
										<p className="font-semibold text-foreground">{label}</p>
										<p className="text-sm text-muted-foreground">{detail}</p>
									</div>
								</div>
							))}
						</div>
					</div>

					<figure className="relative mx-auto hidden w-full max-w-md overflow-hidden rounded-2xl shadow-lg lg:block">
						<Image
							src="/openday/openday-locandina.jpg"
							alt="Locandina Open Day Santa Marta"
							width={1024}
							height={1024}
							className="h-auto w-full"
							sizes="(min-width: 1024px) 420px, 90vw"
						/>
					</figure>
				</div>
			</PageSection>

			<PageSection className="pt-0">
				<div className="rounded-2xl bg-[#f0f8ff] px-6 py-10 text-center md:px-12">
					<h2 className="text-3xl font-bold">
						<span className="text-foreground">Ti aspettiamo in </span>
						<span className="text-(--card-foreground)">Via Paganico</span>
					</h2>
					<p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
						Non serve iscriversi in anticipo: vieni in uno dei tre sabati,
						portati il tempo di fare due chiacchiere con noi.
					</p>

					<div className="mt-6 flex flex-col items-center gap-2 text-foreground">
						<p className="inline-flex items-center gap-2 font-medium">
							<MapPin className="size-4 text-(--card-foreground)" aria-hidden />
							{schoolContact.addressShort}
						</p>
					</div>

					<div className="mt-8 flex flex-wrap items-center justify-center gap-3">
						<Link
							href={mapsUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-(--btn-bg) px-6 font-semibold text-(--btn-foreground) shadow-md transition hover:bg-(--btn-hover)"
						>
							<MapPin className="size-4" aria-hidden />
							Indicazioni
						</Link>
						<Link
							href="/contatti"
							className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-(--card-foreground) bg-white px-6 font-semibold text-foreground transition hover:bg-[#e8f6ff]"
						>
							Tutti i contatti
						</Link>
					</div>
				</div>
			</PageSection>
		</div>
	);
}
